import React, { useCallback, useRef } from 'react';
import { FieldValues, Message, Path, PathValue, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path';
import { UseMutateAsyncFunction } from 'react-query';
import ReactQuill, { Quill } from 'react-quill';
import { AxiosError } from 'axios';
import imageCompression from 'browser-image-compression';
import * as Emoji from 'quill-emoji';

import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';

import { PublicFile } from '../../models/file';
import { HttpException } from '../../models/httpException';
import { FormGroup } from '../FormGroup';

Quill.register('modules/emoji', Emoji);

type TextEditorProps<T extends FieldValues> = {
  id: FieldPath<T>;
  setValue: UseFormSetValue<T>;
  label: React.ReactNode;
  watch: UseFormWatch<T>;
  trigger: UseFormTrigger<T>;
  validationError?: string | Message | undefined;
  placeholder?: string;
  uploadFileFunction?: UseMutateAsyncFunction<PublicFile, AxiosError<HttpException>, { image: File }, unknown>;
};

export const TextEditor = <T extends FieldValues>({
  id,
  label,
  validationError,
  watch,
  setValue,
  placeholder,
  trigger,
  uploadFileFunction,
}: TextEditorProps<T>) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const handleEditorStateChange = (editorState: PathValue<T, Path<T>>) => {
    setValue(id, editorState);
    trigger(id);
  };

  const uploadImage = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      if (uploadFileFunction) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedBlob = await imageCompression(file, options);
        const compressedFile = new File([compressedBlob], file.name, {
          type: compressedBlob.type,
          lastModified: Date.now(),
        });
        return uploadFileFunction({ image: compressedFile });
      }
    },
    [uploadFileFunction],
  );

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      if (input.files?.length) {
        const file = input.files[0];
        const imageUrl = (await uploadImage(file))?.url;
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection();
        if (range) {
          editor?.insertEmbed(range?.index, 'image', imageUrl);
        }
      }
    };
    input.click();
  }, [uploadImage]);

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, false] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        ['link', 'image', 'video'],
        ['emoji'],
        ['clean'],
      ],
      handlers: { image: uploadFileFunction ? handleImageUpload : undefined },
    },
    'emoji-toolbar': true,
    'emoji-textarea': true,
    'emoji-shortname': true,
  };

  const editorContent = watch(id);

  return (
    <FormGroup label={label} inputId={id} validationError={validationError}>
      <ReactQuill
        id={id}
        theme="snow"
        value={editorContent}
        onChange={handleEditorStateChange}
        modules={modules}
        ref={quillRef}
        placeholder={placeholder}
      />
    </FormGroup>
  );
};
